import { IconTrash } from '@tabler/icons-react';
import { useEffect } from 'react';
import NSIcon from 'bricks/NSIcon/NSIcon';
import { NSCard, NSSwitch } from 'bricks';
import { IOptionAnyValue } from 'bricks/types';
import { Row } from 'reactstrap';
import { IDeal, IProFormaSelectionOption } from 'views/Reports/types';
import { useGetProFormasByDeal } from 'queries/ProForma/useGetProFormasByDeal';
import { PRO_FORMA_STATUS } from 'views/ProFormaHome/constants';
import ProFormaSelector from '../ProFormaSelector/ProFormaSelector';

export interface ISelectedDealProps {
    deal: IDeal;
    selectedProFormaId?: number;
    useProjectActuals?: boolean;
    onChangeProFormaSelection: (newSelection: IProFormaSelectionOption) => void;
    onRemoveDealSelection: () => void;
    onToggleUseProjectActuals?: (checked: boolean) => void;
    onResolveProFormaModelId?: (proFormaModelId?: number) => void;
}

const SelectedDeal = ({
    deal, selectedProFormaId, useProjectActuals = false, onChangeProFormaSelection,
    onRemoveDealSelection, onToggleUseProjectActuals, onResolveProFormaModelId,
}: ISelectedDealProps) => {
    const { data: proFormas } = useGetProFormasByDeal(
        { dealId: deal.id, proFormaStatusIds: [PRO_FORMA_STATUS.SUBSTANTIALLY_COMPLETE] },
    );

    const selectedProForma = proFormas?.find(pf => pf.id === selectedProFormaId);
    const hasLinkedProject = selectedProForma?.subprojectId != null;

    const selectedProFormaModelId = selectedProForma?.proFormaModelId ?? undefined;
    useEffect(() => {
        if (selectedProFormaModelId != null) {
            onResolveProFormaModelId?.(selectedProFormaModelId);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedProFormaModelId]);

    return (
        <NSCard className="NSCard--level-4 p-2 px-3 m-0">
            <Row className="d-flex flex-column flex-xl-row m-0 justify-content-between align-items-xl-center">
                <div className="m-0 mb-2 mb-xl-0">
                    <h4 className="text-white mb-0 text-break">{deal.dealName}</h4>
                    <div>
                        {deal.assetType && <span>{`${deal.assetType} • `}</span>}
                        <span>{deal.dealType}</span>
                    </div>
                </div>
                <div className="d-flex flex-column flex-xl-row m-0 py-1 align-items-start align-items-xl-center gap-2 gap-xl-3">
                    <div className="w-100 mr-2 mb-2 mb-xl-0">
                        <ProFormaSelector
                            dealId={deal.id}
                            proFormaId={selectedProFormaId}
                            onChangeProFormaSelection={(option: IOptionAnyValue) => onChangeProFormaSelection({
                                ...option,
                                proFormaModelId: proFormas?.find(pf => pf.id === option.value)?.proFormaModelId ?? undefined,
                            })}
                            dropdownClassName="Deal-selector__dropdown"
                            showDefaultOption
                        />
                    </div>
                    <div className="d-flex align-items-center flex-shrink-0">
                        {hasLinkedProject && onToggleUseProjectActuals && (
                            <>
                                <span className="text-nowrap">Use project actuals</span>
                                <NSSwitch
                                    id={`use-project-actuals-${deal.id}`}
                                    containerClassName="mx-2"
                                    name={`use-project-actuals-${deal.id}`}
                                    checked={useProjectActuals}
                                    onChange={e => onToggleUseProjectActuals(e.target.checked)}
                                    data-testid={`use-project-actuals-switch-${deal.id}`}
                                />
                            </>
                        )}
                        <NSIcon icon={IconTrash} data-testid="trash-icon" size="lg" className="cursor--pointer" onClick={onRemoveDealSelection} />
                    </div>
                </div>
            </Row>
        </NSCard>
    );
};

export default SelectedDeal;
